import { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'antd';
import Button from 'components/button';
import Icon, { ICONS } from 'components/icons';
import Notification from 'components/notification';
import { DropdownItem, DropdownMenu, ExportButtonContainer, SpinSvg } from './styles';

// Starburst spinner: 8 bars at 45° increments, uniform opacity.
const BARS = Array.from({ length: 8 }, (_, i) => i);
const SpinnerIcon = (): JSX.Element => (
  <SpinSvg width="16" height="16" viewBox="0 0 24 24" fill="none">
    {BARS.map((i) => (
      <rect
        key={i}
        x="11"
        y="2"
        width="2"
        height="6"
        rx="1"
        fill="currentColor"
        transform={`rotate(${i * 45} 12 12)`}
      />
    ))}
  </SpinSvg>
);

export interface ExportMenuItem {
  key: string;
  label: string;
  icon?: keyof typeof ICONS;
  iconColor?: string;
  disabled?: boolean;
  onClick?: () => void;
  'data-testid'?: string;
}

type DropdownPlacement = React.ComponentProps<typeof Dropdown>['placement'];

interface Props {
  label?: string;
  loadingLabel?: string;
  disabled?: boolean;
  isLoading?: boolean;
  /** Provide items to render a dropdown menu; omit for a direct-click button. */
  items?: ExportMenuItem[];
  /** Called on click when no items provided. Shows a placeholder loader if omitted. */
  onExport?: () => void;
  variant?: 'secondary' | 'primary';
  /** 'responsive' shrinks the button below the lg breakpoint (Figma tablet/mobile spec). */
  size?: 'default' | 'responsive';
  menuPlacement?: DropdownPlacement;
  'data-testid'?: string;
}

const NOT_IMPLEMENTED_MSG = 'This export is not yet available. Check back soon.';
const PLACEHOLDER_LOADING_MS = 1500;

const ExportButton = ({
  label = 'Export',
  loadingLabel,
  disabled,
  isLoading = false,
  items,
  onExport,
  variant = 'secondary',
  size = 'default',
  menuPlacement = 'bottomRight',
  'data-testid': testId = 'export-button',
}: Props): JSX.Element => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [placeholderLoading, setPlaceholderLoading] = useState(false);
  const placeholderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (placeholderTimerRef.current !== null) clearTimeout(placeholderTimerRef.current);
    };
  }, []);

  const effectiveLoading = isLoading || placeholderLoading;
  const effectiveLabel = effectiveLoading ? loadingLabel ?? label : label;

  const runPlaceholder = () => {
    setPlaceholderLoading(true);
    placeholderTimerRef.current = setTimeout(() => {
      setPlaceholderLoading(false);
      Notification({ type: 'info', text: NOT_IMPLEMENTED_MSG });
    }, PLACEHOLDER_LOADING_MS);
  };

  if (!items?.length) {
    const handleClick = () => {
      if (effectiveLoading) return;
      if (onExport) {
        onExport();
      } else {
        runPlaceholder();
      }
    };

    return (
      <ExportButtonContainer
        $variant={variant}
        $size={size}
        data-loading={effectiveLoading ? 'true' : undefined}
      >
        <Button
          $secondary
          size="small"
          disabled={disabled || effectiveLoading}
          onClick={handleClick}
          data-testid={testId}
        >
          {effectiveLoading ? <SpinnerIcon /> : <Icon icon="Download" />}
          {effectiveLabel}
        </Button>
      </ExportButtonContainer>
    );
  }

  const overlay = (
    <DropdownMenu role="menu" data-testid={`${testId}-menu`}>
      {items.map((item) => (
        <DropdownItem
          key={item.key}
          role="menuitem"
          disabled={item.disabled}
          aria-label={item.label}
          data-testid={item['data-testid']}
          onClick={() => {
            setMenuOpen(false);
            if (item.onClick) {
              item.onClick();
            } else if (!item.disabled) {
              runPlaceholder();
            }
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              flexShrink: 0,
            }}
          >
            <Icon
              icon={item.icon ?? 'Download'}
              style={{
                width: '20px',
                height: '20px',
                ...(item.iconColor ? { color: item.iconColor } : {}),
              }}
            />
          </span>
          <span>{item.label}</span>
        </DropdownItem>
      ))}
    </DropdownMenu>
  );

  return (
    <ExportButtonContainer
      $variant={variant}
      $size={size}
      data-loading={effectiveLoading ? 'true' : undefined}
    >
      <Dropdown
        overlay={overlay}
        trigger={['click']}
        disabled={disabled || effectiveLoading}
        visible={effectiveLoading ? false : menuOpen}
        onVisibleChange={(v) => {
          if (!effectiveLoading) setMenuOpen(v);
        }}
        placement={menuPlacement}
      >
        <Button
          $secondary
          size="small"
          disabled={disabled || effectiveLoading}
          data-testid={testId}
          aria-haspopup="true"
          aria-expanded={menuOpen}
        >
          {effectiveLoading ? <SpinnerIcon /> : <Icon icon="Download" />}
          {effectiveLabel}
        </Button>
      </Dropdown>
    </ExportButtonContainer>
  );
};

export default ExportButton;
